import { useRef } from "react";
import { IActivityDetails, IActivityMetricDetails } from "@Models/IActivityDetails";
import { GroupContext, NoContext, QueryKeys, SystemContext, UserContext } from "@Constants";
import SessionManager from "@Core/session";
import { IRolePayload } from "@Models/IRoleDetails";
import Pako from "pako";
import { IRoleSignature } from "@Models/IRoleSignature";
import { IOptionPayload } from "@Models/IOptionDefinitionDetails";
import { IUserGroupDetails } from "@Models/IUserGroupDetails";
import appQueryClient from "@AppQueryClient";
import RoleService from "@Services/RoleService";
import moment from "moment";
import { isNil } from "./utils/validators";
import { ITerm } from "@Models/ITerms";
import { IUserDetails } from "@Models/IUserDetails";

export * from "./utils/dateTime";
export * from "./utils/dom";
export * from "./utils/draftjs";
export * from "./utils/formatters";
export * from "./utils/hooks";
export * from "./utils/mappers";
export * from "./utils/numbers";
export * from "./utils/pdfUtils";
export * from "./utils/strings";
export * from "./utils/validators";
export * from "./utils/zod";

/**
 * Add a hook to a function component which behaves like a constructor.
 * Useful when a component requires run-once functionality.
 * Must be added at the very top of a function component.
 */
export const useRunOnce = (callBack = () => {}) => {
    const complete = useRef(false);
    if (complete.current) return;
    callBack();
    complete.current = true;
};

/**
 * Get the base URL of the server.
 */
export const getServerUrl = (): string => {
    return window.location.protocol + "//" + location.host.split(":")[0] + ":" + window.location.port;
};

/**
 * Gets the current Environment
 */
declare const window: any; // eslint-disable-line @typescript-eslint/no-explicit-any
export const getEnvironment = (): string => window?.environment ?? "";

/**
 * Checks if currently in the development environment
 */
export const isDevelopment = (): boolean => {
    const environment = getEnvironment()?.toLowerCase();
    return /sandbox|dev/.test(environment);
};

export const checkCapWithContext = (capability: string, context: number, instance = "") => {
    if (instance === getSystemId() && context !== SystemContext) return false;
    return hasCapability(capability, context, instance);
};

/**
 * Return true if the session user has the given capability on the context and instance provided.
 * Where a group context is provided, it may be tested as a child group under the purview of the user's role assignments.
 * @param capability
 * @param contextId
 * @param instanceId
 */
export const hasCapability = (capability: string, contextId: number, instanceId = "") => {
    const allRoles = appQueryClient.getQueryData<IRolePayload>([QueryKeys.Roles]);
    const roleIndices = new Map(allRoles?.roles?.map((role, index) => [role.roleId, index]));
    const userRoles = getUserSignature();
    if (allRoles === undefined || userRoles === undefined) {
        return false;
    }

    // Return early if no role assignments exist for user.
    if (userRoles?.length === 0 || capability === null) {
        return false;
    }
    // Look up the capability
    const cap = allRoles?.capabilities?.find((x) => x?.name === capability);

    if (cap === null) return false;

    for (let i = 0; i < userRoles?.length; i++) {
        const userRole = userRoles[i];
        if (userRole === null) continue;

        const roleDefinition = allRoles?.roles?.find((x) => x.roleId === userRole.roleId);
        if (isNil(roleDefinition)) continue;

        const capDef = roleDefinition?.roleCapabilities?.find((x) => x.capabilityId === cap.capabilityId);

        // Test the capability context id from the user's role
        switch (capDef.contextId) {
            case NoContext:
                break;

            case SystemContext:
                return true;

            case GroupContext:
                // For a group context capability, the user must be a member of the group specified
                if (
                    contextId === GroupContext &&
                    userRole.instanceIds?.includes(instanceId) &&
                    userRole.contextId === GroupContext
                ) {
                    return true;
                }
                break;

            case UserContext:
                // For a user context capability, the user must actually be the user identified
                if (contextId === UserContext && instanceId === SessionManager.getServiceUser().userId) {
                    return true;
                }
                break;
        }
    }
    return false;
};

export const hasCapabilityContext = (capability: string, contextId: number) => {
    const lowestContext = findLowestContext(capability);
    if (lowestContext === NoContext) return false;

    return lowestContext <= contextId;
};

/**
 * Return lowest capability context available if the user has the given capability.
 * Returns NoContext (0) if no context is available
 * @param capability
 */
export const findLowestContext = (capability: string) => {
    const allRoles = appQueryClient.getQueryData<IRolePayload>([QueryKeys.Roles]);
    const userRoles = getUserSignature();
    if (allRoles === undefined || userRoles === undefined) {
        return NoContext;
    }

    // Return early if no role assignments exist for user.
    if (userRoles?.length === 0 || capability === null) {
        return NoContext;
    }
    // Look up the capability
    const cap = allRoles?.capabilities?.find((x) => x.name === capability);
    if (cap === null) return NoContext;

    // Find all applicable capabilities.
    let lowestContext = 100;
    for (let i = 0; i < allRoles?.roles?.length; i++) {
        const roleDetails = allRoles.roles[i];
        const index = roleDetails.roleCapabilities.findIndex(
            (y) =>
                y.capabilityId === cap.capabilityId &&
                y.contextId > NoContext &&
                userRoles.map((x) => x.roleId).includes(roleDetails.roleId)
        );

        if (index !== -1) {
            const capContextId = roleDetails.roleCapabilities[index].contextId;
            if (capContextId < lowestContext) {
                lowestContext = capContextId;
            }
        }
    }
    return lowestContext === 100 ? NoContext : lowestContext;
};

/**
 * Get the named user option from the query cache, the query for which is first called in AppRoute.
 * @param name
 */
export const getUserOption = (name: string) => {
    const userOptions = appQueryClient.getQueryData<IOptionPayload>([QueryKeys.UserOptions]);
    if (userOptions === null) return null;
    const definition = userOptions?.definitions?.find((x) => x.name.toLowerCase() === name.toLowerCase());
    if (definition === null) return null;
    const instance = userOptions?.instances?.find((x) => x.optionId === definition.optionId);
    return instance?.value ?? null;
};

/**
 * Return a full path for the Url provided.
 * @param url
 */
export const transformUrl = (url: string) => {
    return window.location.origin.replace(/\/$/, "") + url;
};

/**
 * Return the activity label of the activity provided, overriding with custom meta where appropriate.
 * @param activity
 */
export const getActivityLabel = (activity?: IActivityDetails): string => {
    if (activity === undefined || activity == null) return "";
    return activity?.activityMeta?.title ? prettifyName(activity?.activityMeta?.title) : activity?.activityLabel;
};

/**
 * Get a named metric from an activity, returning a percentile where appropriate.
 * @param metric
 * @param activity
 */
export const getActivityMetric = (metric: string, activity: IActivityDetails): number => {
    if (activity === undefined || activity == null) return 0;

    // duration related metrics
    switch (metric) {
        case "duration":
            return activity.duration;

        case "duration-median":
            return activity.durationMedian;

        case "duration-percentile":
            return activity.durationQuantile * 100;

        default:
        // continue to activity metrics check
    }

    if (activity.activityMetrics.length === 0) return 0;

    // Helper to check if metrics contain angular data
    const hasAngularMetrics = activity.activityMetrics.some((m) => m.name.startsWith("angular-"));

    // metrics from the activityMetrics array
    switch (metric) {
        case "acceleration":
            const acceleration = getMetrics(activity.activityMetrics, "acceleration-mean");
            if (!acceleration.length) return 0;
            return (
                (acceleration.map((x) => x.quantile).reduce((prev, next) => prev + next) / acceleration.length) * 100
            );

        case "handedness":
            if (+activity.trackingIds === 1) return 0;
            const dsj = getMetrics(activity.activityMetrics, "dsj-measurements");
            if (dsj.length < 2) return 0;
            if (+dsj[0].value + +dsj[1].value === 0) return 0;
            return (dsj[0].value / (dsj[0].value + dsj[1].value)) * 100;

        case "distance":
            const distanceTotal = getMetrics(activity.activityMetrics, "distance-total");
            if (!distanceTotal.length) return 0;
            return distanceTotal.map((x) => x.value).reduce((prev, next) => prev + next);

        case "distance-global":
            const distanceGlobal = getMetrics(activity.activityMetrics, "distance-total");
            if (!distanceGlobal.length) return 0;
            return distanceGlobal.map((x) => x.valueMedian).reduce((prev, next) => prev + next);

        case "distance-percentile":
            const distPercentile = getMetrics(activity.activityMetrics, "distance-total");
            if (!distPercentile.length) return 0;
            return (
                (distPercentile.map((x) => 1 - x.quantile).reduce((prev, next) => prev + next) /
                    distPercentile.length) *
                100
            );

        case "speed":
            const speedMetricName = hasAngularMetrics ? "angular-speed-mean" : "speed-mean";
            const speed = getMetrics(activity.activityMetrics, speedMetricName);
            if (!speed.length) return 0;
            return (speed.map((x) => x.quantile).reduce((prev, next) => prev + next) / speed.length) * 100;

        case "smoothness":
            // Try angular metrics first if available, fall back to linear
            const smoothnessMetricName = hasAngularMetrics ? "angular-dsj-mean" : "dsj-mean";
            const smoothness = getMetrics(activity.activityMetrics, smoothnessMetricName);
            if (!smoothness.length) return 0;
            return (smoothness.map((x) => x.quantile).reduce((prev, next) => prev + next) / smoothness.length) * 100;

        case "precision":
            // Try angular metrics first if available, fall back to linear
            const precisionMetricName = hasAngularMetrics ? "angular-acceleration-deviation" : "acceleration-deviation";
            const precision = getMetrics(activity.activityMetrics, precisionMetricName);
            if (!precision.length) return 0;
            return (precision.map((x) => x.quantile).reduce((prev, next) => prev + next) / precision.length) * 100;

        default:
            return 0;
    }
};

/**
 * Return a pretty duration for a given number of seconds.
 */
export const getDuration = (num?: number): string => {
    if (!num) {
        return "0 Mins";
    }
    const hours: number = Math.floor(num / 3600);
    const mins: number = Math.floor((num % 3600) / 60);
    if (+hours === 0 && +mins === 0) return "< 1 Min";
    const hourPlural: string = hours <= 1 ? "" : "s";
    const minPlural: string = mins <= 1 ? "" : "s";
    return String((hours > 0 ? hours + " Hour" + hourPlural + " " : "") + (mins > 0 ? mins + " Min" + minPlural : 0));
};

/**
 *
 * Extracts the duration in seconds directly from a file
 */
export const extractDurationFromFile = (file: Blob): Promise<number> => {
    const url = URL.createObjectURL(file);

    return new Promise((resolve, reject) => {
        if (!url) reject("Could not access video");
        const video = document.createElement("video");
        const source = document.createElement("source");
        source.src = url;
        video.appendChild(source);
        video.onloadedmetadata = () => {
            resolve(video.duration);
        };
    });
};

/**
 * Return the suffix string required for an ordinal representation of any number, e.g. 1st, 2nd, 3rd, 4th, etc.
 */
export const nth = (d: number): string => {
    if (d > 3 && d < 21) {
        return "th";
    }
    switch (d % 10) {
        case 1:
            return "st";
        case 2:
            return "nd";
        case 3:
            return "rd";
        default:
            return "th";
    }
};

/**
 * Get the sum of the char codes for the string provided.
 * @param string
 */
export const getCharacterSum = (string: string) => {
    let i: number,
        sum = 0;
    for (i = 0; i < string.length; i++) {
        sum += string.charCodeAt(i) * (i + 1);
    }
    return sum;
};

/**
 * Given an array of strings, produce a single hash of the character codes.
 * @param array
 */
export const getCharacterSumHash = (array: string[]) => {
    let i: number,
        sum = 0;
    for (i = 0; i < array.length; i++) {
        const cs = getCharacterSum(array[i]);
        sum = sum + 65027 / cs;
    }
    return ("" + sum).slice(0, 16);
};

/*
 * Return array of metric details by named attribute.
 */
export const getMetrics = (metrics: IActivityMetricDetails[], name: string) => {
    return metrics.filter((x) => x.name === name);
};

/**
 * Return a pretty padded time for a given duration.
 */
export const formatDuration = (value: number) => {
    const date = new Date(value * 1000);
    if (isNaN(date?.getTime())) return "";
    return date?.toISOString().substr(value > 3600 ? 11 : 14, value > 3600 ? 8 : 5);
};

/**
 * Return whether the provided Date string is a valid ISO8601 DateTime.
 */
export const isValidIsoDate = (str: string): boolean => {
    if (str === "") return false;
    if (!/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)((-(\d{2}):(\d{2})|Z)?)$/.test(str)) return false;
    const d = new Date(str);
    return d instanceof Date; // valid date
};

/**
 * Return whether the date is before ten years ago. Ensures no one below the age of 10 can register.
 */
export const disableUnder10YearsAgo = (current) => {
    const tenYearsAgo = moment().subtract(10, "years");
    return current >= tenYearsAgo;
};

/**
 * Return whether the date is in the past.
 * @param current date to be checked
 */
export const disablePastDates = (current: moment.Moment) => {
    // Can't select days before today
    return current && current < moment().startOf("day");
};

/**
 * Remap a value from one range to another. Result is un-clamped.
 */
export const remap = (value: number, fromLow: number, fromHigh: number, toLow: number, toHigh: number): number => {
    return toLow + ((toHigh - toLow) * (value - fromLow)) / (fromHigh - fromLow);
};

/**
 * Capitalize the first letter of each separate word in the string provided.
 */
export const prettifyName = (string: string) => {
    if (string === undefined || string === null) return "";
    string = string?.toLowerCase();
    return string?.replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase());
};

/**
 * Converts camelCase to Title Case and also strips leading and trailing spaces
 */
export const camelToTitle = (camelCase: string) =>
    camelCase
        ?.replace(/([A-Z])/g, (match) => ` ${match}`)
        ?.replace(/^./, (letter) => letter.toUpperCase())
        ?.trim();

/**
 *
 * Converts any string to PascalCase
 */
export const toPascalCase = (str: string) =>
    `${str}`
        .toLowerCase()
        .replace(new RegExp(/[-_]+/, "g"), " ")
        .replace(new RegExp(/[^\w\s]/, "g"), "")
        .replace(new RegExp(/\s+(.)(\w*)/, "g"), (_, $2, $3) => `${$2.toUpperCase() + $3}`)
        .replace(new RegExp(/\w/), (s) => s.toUpperCase());

/**
 *
 * Converts any string to CamelCase
 */
export const toCamelCase = (str: string) =>
    `${str}`
        .replace(new RegExp(/[-_]+/, "g"), " ")
        .replace(new RegExp(/[^\w\s]/, "g"), "")
        .replace(/\s(.)/g, ($1) => $1.toUpperCase())
        .replace(/\s/g, "")
        .replace(/^(.)/, ($1) => $1.toLowerCase());

/**
 * Clamp a number between min and max values.
 */
export const clamp = (value: number, min: number, max: number): number => {
    return Math.min(Math.max(value, min), max);
};

/*
 * Linear interpolation between two or three hex colours.
 * Where three colours are provided the alpha value will be used to lerp across all three.
 * Colours must be provided as full html hex strings, e.g. "#FFFFFF"
 */
export const lerpColor = (a: string, b: string, c: string, alpha: number): string => {
    if (c != null) {
        if (alpha >= 0.5) {
            a = b;
            b = c;
            alpha = (alpha - 0.5) * 2;
        } else {
            alpha *= 2;
        }
    }

    const ah = +a.replace("#", "0x"),
        ar = ah >> 16,
        ag = (ah >> 8) & 0xff,
        ab = ah & 0xff,
        bh = +b.replace("#", "0x"),
        br = bh >> 16,
        bg = (bh >> 8) & 0xff,
        bb = bh & 0xff,
        rr = ar + alpha * (br - ar),
        rg = ag + alpha * (bg - ag),
        rb = ab + alpha * (bb - ab);

    return "#" + (((1 << 24) + (rr << 16) + (rg << 8) + rb) | 0).toString(16).slice(1);
};

export const getApplicationLoader = (): HTMLElement => {
    return document.getElementById("applicationLoader");
};

/**
 * Show main application loader.
 * */
export const showApplicationLoader = () => {
    const loader = getApplicationLoader();
    if (loader) {
        loader.className = "";
    }
};

/**
 * Hide main application loader.
 * */
export const hideApplicationLoader = () => {
    const loader = getApplicationLoader();
    if (loader) {
        loader.className = "hidden";
    }
};

/**
 * Clone object.
 * @param object input object.
 */
export const clone = <T>(object: T): T => {
    return JSON.parse(JSON.stringify(object));
};

/**
 * Return the user's roles and groups as an object from their signature cookie.
 */
export const getUserSignature = (): IRoleSignature[] => {
    const sig = (document.cookie.match(/^(?:.*;)?\s*Signature\s*=\s*([^;]+)(?:.*)?$/) || [undefined, null])[1];
    if (sig !== null) {
        const raw = window.atob(decodeURIComponent(sig));
        const charData = raw.split("").map(function (x) {
            return x.charCodeAt(0);
        });
        const binData = new Uint8Array(charData);
        const deflated = Pako.ungzip(binData, { to: "string" }).replace(/\0/g, "");
        const parsed = JSON.parse(deflated);
        const result: IRoleSignature[] = [];
        for (const i in parsed) {
            result.push(parsed[i]);
        }
        return result.flat();
    }
    return [];
};

/**
 * Return the user's signature string from their signature cookie.
 */
const getUserSignatureString = (): string | null => {
    // find current signature using regex
    const sig = (document.cookie.match(/^(?:.*;)?\s*Signature\s*=\s*([^;]+)(?:.*)?$/) || [undefined, null])[1];
    return sig;
};

/**
 * Refresh the user's signature cookie.
 *
 * - This expires the user's current signature cookie
 * - Initiates a simple request and once the request is completed checks the
 * user's signature cookie to see if it has been updated.
 */
export async function refreshUserSignature(): Promise<boolean> {
    // find current signature
    const sig = getUserSignatureString();

    if (!isNil(sig)) {
        // expire current signature if exists
        document.cookie = `Signature=${sig}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }

    /**
     * Once expired any request will update the signature. This one is a
     * simple request.
     */
    const roleService = new RoleService();
    const result = await roleService.getAll(true);

    // check if request was successful
    if (result.hasErrors) {
        return false;
    }

    // get updated signature
    const updatedSig = getUserSignatureString();

    // compare signatures
    return sig !== updatedSig;
}

/**
 * Decode and write the logged-in user's entire roles and capability mappings to the console.
 */
export const debugUserSignature = () => {
    const allRoles = appQueryClient.getQueryData<IRolePayload>([QueryKeys.Roles]);
    const allGroups = appQueryClient.getQueryData<IUserGroupDetails[]>([QueryKeys.UserGroups]);
    if (allGroups === undefined || allRoles === undefined) return;
    const userRoles = getUserSignature();
    const details = userRoles.map((roleDetails) => {
        const role = allRoles.roles.find((y) => y.roleId === roleDetails.roleId);
        if (isNil(role)) return null;
        const instances = [];
        const contextName =
            role?.contextId === GroupContext ? "group" : role.contextId === SystemContext ? "system" : "user";

        switch (role.contextId) {
            case GroupContext:
                roleDetails.instanceIds.forEach((id) => {
                    const groupIndex = allGroups?.findIndex((g) => g.groupId === id);
                    if (groupIndex !== -1) {
                        instances.push({ instanceId: id, name: allGroups[groupIndex].name });
                    }
                });
                break;
            case UserContext:
                instances.push({
                    instanceId: SessionManager.getServiceUser().userId,
                    name: SessionManager.getServiceUser().fullName,
                });
                break;
            case SystemContext:
                instances.push({ instanceId: getSystemId(), name: "System" });
                break;
        }

        const caps = role.roleCapabilities.map((roleCap) => {
            return {
                capability: allRoles.capabilities?.find((x) => x.capabilityId === roleCap.capabilityId),
                contextId: roleCap.contextId,
            };
        });

        return {
            roleId: role.roleId,
            role: role.name,
            context: contextName,
            instances: instances,
            capabilities: caps,
        };
    });

    // eslint-disable-next-line no-console
    console.log(
        "User roles",
        details.filter((x) => x !== null)
    );
};

/**
 * Return the hashed system id.
 */
export const getSystemId = () => {
    return SessionManager.getServiceUser()?.systemId;
};

/**
 * Generate a Guid.
 */
export const generateGuid = () => {
    let d = new Date().getTime();
    let d2 = (typeof performance !== "undefined" && performance.now && performance.now() * 1000) || 0; //Time in microseconds since page-load or 0 if unsupported
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
        let r = Math.random() * 16;
        if (d > 0) {
            r = (d + r) % 16 | 0;
            d = Math.floor(d / 16);
        } else {
            r = (d2 + r) % 16 | 0;
            d2 = Math.floor(d2 / 16);
        }
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
};

export const isDefaultBranding: boolean = window?.["branding"] !== "hologic";

/**
 * Scale a number logarithmically.
 * @param n
 * @param base
 */
export const log = (n: number, base: number = Math.exp(1)) => {
    if (base === Math.exp(1)) return Math.log(n);
    return Math.log(n) / Math.log(base);
};

/**
 * Scale a number by its anti-log.
 * @param n
 * @param base
 */
export const antilog = (n, base = Math.exp(1)) => {
    if (base === Math.exp(1)) return Math.exp(n);
    return Math.pow(base, n);
};

/**
 * Lighten the input colour by a value between 1 and 255.
 * @param color
 * @param amount
 */
export const lightenColor = (color: string, amount: number): string => {
    color = color.replace(/^#/, "");
    const num = parseInt(color, 16);
    let r = Math.min(255, Math.max(0, (num >> 16) + amount));
    let b = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amount));
    let g = Math.min(255, Math.max(0, (num & 0x0000ff) + amount));
    const newColor = g | (b << 8) | (r << 16);
    return "#" + newColor.toString(16);
};

/**
 * Classnames to assign to a progress bar with varying bar colours
 * @param percent
 */
export const getProgressClass = (percent) => {
    if (percent <= 20) return "progress-red";
    if (percent <= 50) return "progress-blue";
    return "progress-green";
};

/**
 * Generates a media URL baseed on provided media GUID and descriptor
 * @param mediaGuid the media guid (unique identifier assigned to each media file)
 * @param mediaDescriptor the descriptor for the media file, including the file type
 */

export const getMediaUrl = (mediaGuid: string, mediaDescriptor: string) => {
    const url = `https://${window["mediaServiceName"]}/media/${mediaGuid}/${mediaDescriptor}`;
    return url;
};

/**
 * Defers the execution of a function to the next tick of the event loop.
 *
 * This function uses setTimeout with a delay of 0 milliseconds to push the execution
 * of the provided function to the end of the current event loop iteration. This can
 * be useful in scenarios where you need to ensure that a piece of code runs after
 * the current execution context has completed, such as:
 *
 * Dealing with problematic third-party components in React
 *
 * Note: While this function provides a way to defer execution, it's generally
 * recommended to use React's built-in mechanisms (like useEffect, useState) for
 * managing component lifecycle and updates when possible.
 *
 * @template T The type of the value that the deferred function returns
 *
 * @param fn - The function to be deferred. This function
 * can return either a value of type T or a Promise that resolves to a value of type T.
 */
export const deferExecution = async <T>(fn: () => T | Promise<T>): Promise<T> => {
    return new Promise<T>((resolve) => {
        setTimeout(() => {
            resolve(fn());
        }, 0);
    });
};

/**
 * Generates a consistent, human-readable display name for a terms version
 * including type, version number (if multiple versions of same type exist),
 * creation date, and published status.
 *
 * @param term The term item
 * @param termsList Complete list of terms for numbering context
 * @returns Formatted display name string
 */
export const getTermVersionDisplayName = (term: ITerm, termsList: ITerm[]): string => {
    // Group terms by type to determine numbering
    const termsByType = termsList.reduce((acc, t) => {
        const termType = t.termsType || "Unknown";
        if (!acc[termType]) {
            acc[termType] = [];
        }
        acc[termType].push(t);
        return acc;
    }, {} as Record<string, ITerm[]>);

    // Sort each group by creation date
    Object.keys(termsByType).forEach((termType) => {
        termsByType[termType].sort((a, b) => {
            const dateA = new Date(a.createdOn);
            const dateB = new Date(b.createdOn);
            return dateA.getTime() - dateB.getTime();
        });
    });

    const termType = term.termsType || "Unknown";
    const termsOfSameType = termsByType[termType];

    // Only add numbering if there are multiple versions of the same type
    const displayNumber = termsOfSameType.length > 1 ? ` (${termsOfSameType.indexOf(term) + 1})` : "";

    // Format the creation date
    const createdDate = new Date(term.createdOn).toLocaleDateString();

    // Add published indicator if the term is published
    const publishedIndicator = isTermPublished(term) ? " (Published)" : "";

    return `${termType}${displayNumber} - ${createdDate}${publishedIndicator}`;
};

/**
 * Checks if a term version is published by verifying its publishedOn date
 *
 * @param term The term item to check
 * @returns Boolean indicating if the term is published
 */
export const isTermPublished = (term?: ITerm): boolean => {
    if (!term) return false;

    // Check if publishedOn exists
    if (!term.publishedOn) return false;

    // Convert to Date object and check validity
    let publishedDate: Date;

    if (term.publishedOn instanceof Date) {
        publishedDate = term.publishedOn;
    } else {
        // Handle string or any other type by safely converting to string first
        try {
            publishedDate = new Date(String(term.publishedOn));
        } catch (e) {
            return false;
        }
    }

    return !isNaN(publishedDate.getTime());
};

/**
 * Calculates the profile completion percentage based on filled form fields
 *
 * @param userData User details from which to calculate completion
 * @param hasGraduationYearField Boolean indicating whether to include expected graduation year in fields to check
 * @param fieldsToCheck Optional list of fields to check (defaults to standard profile fields)
 * @returns Percentage of completion as an integer between 0-100
 */
export const calculateFormCompletion = (
    userData: IUserDetails | null,
    hasGraduationYearField: boolean = false,
    fieldsToCheck: (keyof IUserDetails)[] = [
        "fullName",
        "nickName",
        "email",
        "dateOfBirth",
        "gender",
        "organisation",
        "countryCode",
        "specialty",
        "yearsTraining",
        "medicalSchool",
    ]
): number => {
    if (!userData) return 0;

    // Add expectedGraduationYear to fields if hasGraduationYearField is true
    const fields = hasGraduationYearField ? [...fieldsToCheck, "expectedGraduationYear"] : fieldsToCheck;

    // Count how many fields are filled
    const filledFields = fields.filter((field) => {
        const value = userData[field];
        return value !== null && value !== undefined && value !== "";
    });

    // Calculate percentage (rounded to nearest integer)
    return Math.round((filledFields.length / fields.length) * 100);
};
